import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Alert,
  Table,
  Spinner,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { useAuth } from '../../../contexts/AuthContext';
import dealService from '../../../service/dealService.ts';
import TemplateService from '../../../service/TemplateService.ts';
import CommunicationService from '../../../service/CommunicationService.ts';
import DocumentActionDto from '../../../service/dtos/communication/DocumentActionDto.ts';
import documentsApi from '../../../service/documentsApi';
import DocumentSearchList from '../../../components/Documents/DocumentSearchList';
import { formatNumber } from '../../../utils/formatUtils';
import useOfferCalculations from './hooks/useOfferCalculations';
import { DOCUMENT_TYPE_ADDENDUM } from 'constants/DocumentConstants.ts';
import { DOCUMENT_TYPE_CONTRACT } from 'constants/DocumentConstants.ts';

const PreApprovedTab = ({ 
  deal,
  pendingPreApproval,
  hasSelection,
  selectedOffer,
  offers,
  editedOffers,
  offersLoading,
  selectionLoading,
  confirmPreApprovalLoading,
  formatCurrency: formatCurrencyProp,
  formatDate,
  handleOfferChange,
  handleConfirmPreApproval,
  handleCancelPreApproval,
  // Props for Confirm on Behalf functionality
  handleConfirmOnBehalf,
  generatingLink,
  modalLoading,
  showOffersModal,
  modalError,
  modalOffers,
  modalEditingAmounts,
  modalSelectLoading,
  setModalSelectLoading,
  handleCloseModal,
  handleModalSelectOffer,
  adjustModalAmount,
  isValidModalAmount,
  handleModalAmountChange,
  // Function to reload offers after editing
  loadDealOffers,
  // Functions for magiclink and notifications
  sendPreApprovalSms,
  sendPreApprovalEmails,
  // Blank check status
  hasBlankCheck
}) => {
  const { getToken } = useAuth();
  
  // Use the new formatNumber function for consistent formatting
  const formatCurrency = (amount) => {
    return formatNumber(amount);
  };

  // Format input values for display in editable fields
  const formatInputValue = (value) => {
    if (!value && value !== 0) return '';
    return formatNumber(value);
  };
  
  // Helper functions
  const getOwnerFullName = (owner) => {
    // Try different possible field names
    const firstName = owner.firstName || owner.first_name || '';
    const middleName = owner.middleName || owner.middle_name || '';
    const lastName = owner.lastName || owner.last_name || '';
    
    const fullName = [firstName, middleName, lastName]
      .filter(name => name && name.trim() !== '')
      .join(' ');
    
    return fullName || 'Unknown Owner';
  };

  const getPrimaryEmailContact = (owner) => {
    // Try different possible structures for contacts
    let contacts = owner.contacts || owner.contacs || []; // Note: "contacs" might be a typo in API
    
    if (!Array.isArray(contacts)) {
      return null;
    }

    return contacts.find(contact => 
      contact.isPrimary === true && 
      contact.deleted === false && 
      contact.type === 'EMAIL'
    );
  };
  
  // Estado para la edición de offers en la tabla de Pre-Approved
  const [isEditingOffers, setIsEditingOffers] = useState(false);
  const [editingOffers, setEditingOffers] = useState([]);
  const [originalOffersData, setOriginalOffersData] = useState([]);
  const [updateOffersLoading, setUpdateOffersLoading] = useState(false);
  const [updateOffersError, setUpdateOffersError] = useState(null);
  const [updateOffersSuccess, setUpdateOffersSuccess] = useState(false);
  
  // Estado para el checkbox de Addendum en el modal
  const [addendumChecked, setAddendumChecked] = useState(false);
  
  // Estado para el flujo de Blank Check Upload
  const [modalStep, setModalStep] = useState(1); // 1: select offer, 2: blank check upload
  const [uploadingBlankCheck, setUploadingBlankCheck] = useState(false);
  const [blankCheckUploadError, setBlankCheckUploadError] = useState(null);
  const [blankCheckFile, setBlankCheckFile] = useState(null);

  // Hook for offer calculations and adjustments
  const {
    editedOffers: modalCalculatedOffers,
    initializeOffers,
    getCurrentOffer,
    adjustGrossAdvanceAmount,
    canIncrement,
    canDecrement,
    resetEditedOffers,
    getCurrentGrossAdvanceAmount,
  } = useOfferCalculations(modalOffers);

  // Initialize offers when modal opens
  useEffect(() => {
    if (showOffersModal && modalOffers.length > 0) {
      initializeOffers(modalOffers);
    }
  }, [showOffersModal, modalOffers, initializeOffers]);

  // Función para iniciar la edición de offers
  const handleEditOffers = () => {
    // Guardar los datos originales
    setOriginalOffersData([...offers]);
    // Crear una copia editable de los offers
    setEditingOffers([...offers]);
    setIsEditingOffers(true);
    setUpdateOffersError(null);
    setUpdateOffersSuccess(false);
  };

  // Función para calcular campos dependientes automáticamente
  const calculateDependentFields = (offer, field, value) => {
    // Crear una copia del offer con el campo modificado
    const updatedOffer = { ...offer, [field]: parseFloat(value) || 0 };
    
    // Recalcular campos dependientes automáticamente
    const grossAdvanceAmount = updatedOffer.gross_advance_amount || updatedOffer.grossAdvanceAmount || 0;
    const processingFee = updatedOffer.processing_fee || 0;
    const factor = updatedOffer.factor || 0;
    const termMonths = updatedOffer.term_duration_months || updatedOffer.term || 1;
    
    // Calcular Advance Amount = Gross Advance Amount - Processing Fee
    updatedOffer.advance_amount = grossAdvanceAmount - processingFee;
    updatedOffer.advanceAmount = grossAdvanceAmount - processingFee; // Para compatibilidad
    
    // Calcular Payback (net_payback) = Gross Advance Amount * Factor
    updatedOffer.net_payback = grossAdvanceAmount * factor;
    updatedOffer.netAdvanceAmount = grossAdvanceAmount * factor; // Para compatibilidad
    
    // Calcular Gross Payback = Gross Advance Amount * Factor (mismo que net_payback)
    updatedOffer.gross_payback = grossAdvanceAmount * factor;
    updatedOffer.payback = grossAdvanceAmount * factor; // Para compatibilidad
    
    // Calcular Monthly Payment = Payback / Term Months
    updatedOffer.monthly_payment = updatedOffer.net_payback / termMonths;
    updatedOffer.monthlyGrossMargin = updatedOffer.net_payback / termMonths; // Para compatibilidad
    
    console.log('📊 Offer recalculated:', {
      offerId: offer.id,
      field,
      grossAdvanceAmount,
      processingFee,
      factor,
      termMonths,
      calculated: {
        advance_amount: updatedOffer.advance_amount,
        net_payback: updatedOffer.net_payback,
        monthly_payment: updatedOffer.monthly_payment
      }
    });
    
    return updatedOffer;
  };

  // Función wrapper para manejar cambios en modo pendingPreApproval
  const handlePendingOfferChange = (offerId, field, value) => {
    // Encontrar la oferta actual en editedOffers
    const currentOffer = editedOffers.find(o => o.id === offerId);
    if (!currentOffer) {
      console.warn('⚠️ Current offer not found in editedOffers:', offerId);
      return;
    }
    
    // Calcular todos los campos dependientes
    const updatedOffer = calculateDependentFields(currentOffer, field, value);
    
    console.log('🔄 Updating pending offer:', {
      offerId,
      field,
      value,
      calculatedFields: {
        advance_amount: updatedOffer.advance_amount,
        net_payback: updatedOffer.net_payback,
        gross_payback: updatedOffer.gross_payback,
        monthly_payment: updatedOffer.monthly_payment
      }
    });
    
    // Actualizar el campo principal primero
    handleOfferChange(offerId, field, value);
    
    // Actualizar TODOS los campos calculados (no solo cuando es gross_advance_amount o factor)
    // Esto asegura que los valores estén siempre sincronizados
    setTimeout(() => {
      handleOfferChange(offerId, 'advance_amount', updatedOffer.advance_amount);
      handleOfferChange(offerId, 'advanceAmount', updatedOffer.advance_amount); // Compatibilidad
    }, 0);
    
    setTimeout(() => {
      handleOfferChange(offerId, 'net_payback', updatedOffer.net_payback);
      handleOfferChange(offerId, 'netAdvanceAmount', updatedOffer.net_payback); // Compatibilidad
    }, 10);
    
    setTimeout(() => {
      handleOfferChange(offerId, 'gross_payback', updatedOffer.gross_payback);
      handleOfferChange(offerId, 'payback', updatedOffer.gross_payback); // Compatibilidad
    }, 20);
    
    setTimeout(() => {
      handleOfferChange(offerId, 'monthly_payment', updatedOffer.monthly_payment);
      handleOfferChange(offerId, 'monthlyGrossMargin', updatedOffer.monthly_payment); // Compatibilidad
    }, 30);
  };

  // Función para manejar cambios en los campos de offers (modo edición)
  const handleEditingOfferChange = (offerId, field, value) => {
    setEditingOffers(prevOffers => 
      prevOffers.map(offer => {
        if (offer.id !== offerId) return offer;
        return calculateDependentFields(offer, field, value);
      })
    );
  };

  // Función para cancelar la edición
  const handleCancelOffersEdit = () => {
    setIsEditingOffers(false);
    setEditingOffers([]);
    setOriginalOffersData([]);
    setUpdateOffersError(null);
    setUpdateOffersSuccess(false);
  };


  // Función para resetear el estado del checkbox cuando se cierre el modal
  const handleModalClose = () => {
    setAddendumChecked(false);
    setModalStep(1); // Reset to step 1
    setBlankCheckFile(null);
    setBlankCheckUploadError(null);
    resetEditedOffers(); // Reset calculated offers when closing modal
    if (handleCloseModal) {
      handleCloseModal();
    }
  };

  // Función para actualizar la oferta si fue modificada
  const updateOfferIfModified = async (offerId, offer) => {
    try {
      // Check if the offer was modified
      const currentOffer = getCurrentOffer(offer);
      const wasModified = modalCalculatedOffers[offerId] !== undefined;
      
      if (!wasModified) {
        console.log('Offer was not modified, using original offer ID:', offerId);
        return { offerId, backendOfferId: offer.originalId || offerId, wasUpdated: false };
      }

      console.log('📝 Offer was modified, creating backup and updating with calculated values...');
      const token = getToken();
      
      if (!token || !deal?.privateId) {
        throw new Error('Authentication token or deal privateId not available');
      }

      // Step 1: Create a backup offer with the ORIGINAL values before updating
      console.log('💾 Creating backup offer with original values...');
      const backupOfferData = {
        term_duration_months: offer.term_duration_months || offer.term,
        gross_payback: offer.gross_payback || offer.payback,
        gross_advance_amount: offer.gross_advance_amount || offer.grossAdvanceAmount,
        net_payback: offer.net_payback || offer.netAdvanceAmount,
        monthly_payment: offer.monthly_payment || offer.monthlyGrossMargin,
        advance_amount: offer.advance_amount || offer.advanceAmount,
        factor: offer.factor,
        processing_fee: offer.processing_fee || 0,
        note: `Backup of original offer values before modification (Original ID: ${offer.originalId || offerId})`,
      };

      console.log('💾 Backup offer data:', backupOfferData);

      try {
        const backupResponse = await dealService.createDealOffer(token, deal.privateId, backupOfferData);
        
        if (backupResponse.data && backupResponse.data.id) {
          console.log('✅ Backup offer created successfully with ID:', backupResponse.data.id);
        } else {
          console.warn('⚠️ Backup offer creation returned no ID, continuing anyway...');
        }
      } catch (backupError) {
        console.error('❌ Error creating backup offer:', backupError);
        console.warn('⚠️ Continuing with update despite backup error...');
        // Continue with the update even if backup fails
      }

      // Step 2: Prepare the updated offer data with the NEW calculated values
      const updatedOfferData = {
        term_duration_months: currentOffer.term_duration_months || currentOffer.term,
        gross_payback: currentOffer.gross_payback || currentOffer.payback,
        gross_advance_amount: currentOffer.gross_advance_amount || currentOffer.grossAdvanceAmount,
        net_payback: currentOffer.net_payback || currentOffer.netAdvanceAmount,
        monthly_payment: currentOffer.monthly_payment || currentOffer.monthlyGrossMargin,
        advance_amount: currentOffer.advance_amount || currentOffer.advanceAmount,
        factor: currentOffer.factor,
        processing_fee: currentOffer.processing_fee || 0,
        note: 'Modified offer updated from modal selection',
      };

      console.log('📝 Updating original offer with new data:', updatedOfferData);

      // Step 3: Update the existing offer with the new values
      const backendOfferId = offer.originalId || offerId;
      const response = await dealService.updateDealOffer(token, deal.privateId, backendOfferId, updatedOfferData);
      
      if (!response.data) {
        throw new Error('Failed to update offer');
      }

      console.log('✅ Original offer updated successfully:', response.data);

      return { offerId: backendOfferId, backendOfferId: backendOfferId, wasUpdated: true };
    } catch (error) {
      console.error('❌ Error updating offer:', error);
      throw error;
    }
  };

  // Función wrapper para manejar la selección de offer con flujo de Addendum  
  const handleModalSelectOfferWithAddendum = async (offerId) => {
    // Check if any offer is already being selected
    if (Object.values(modalSelectLoading).some(loading => loading)) {
      console.warn('⚠️ An offer is already being selected, ignoring this click');
      return;
    }
    
    try {
      if (addendumChecked) {
        // Si el checkbox de Addendum está marcado, ejecutar solo el flujo de addendum
        console.log('🔄 Addendum checkbox is checked, executing addendum flow only...');
        const token = getToken();
        await handleAddendumSelectOffer(offerId, token);
      } else {
        // Si el checkbox no está marcado, ejecutar el flujo normal (incluye contract y blank check)
        console.log('🔄 Addendum checkbox is NOT checked, executing normal contract flow with blank check...');
        await handleContractSelectOffer(offerId);
      }
    } catch (error) {
      console.error('❌ Error in offer selection flow:', error);
      throw error;
    }
  };

  // Función wrapper para el flujo de contract que añade la petición deal-addendums
  const handleContractSelectOffer = async (offerId) => {
    // Set loading state at the start
    setModalSelectLoading(prev => ({ ...prev, [offerId]: true }));
    
    try {
      console.log('🔄 Starting contract flow for offer:', offerId);
      
      // Find the offer
      const offer = modalOffers.find(o => o.id === offerId);
      if (!offer) {
        setModalSelectLoading(prev => ({ ...prev, [offerId]: false }));
        throw new Error('Offer not found');
      }

      // Update offer if modified, then select it
      const { offerId: finalOfferId, backendOfferId, wasUpdated } = await updateOfferIfModified(offerId, offer);
      
      if (wasUpdated) {
        console.log('✅ Offer updated, now selecting with ID:', finalOfferId);
      }

      // Get the current offer data for the amount
      const currentOffer = getCurrentOffer(offer);
      const amount = currentOffer.advance_amount || offer.advance_amount;
      
      if (!window.modalSessionData) {
        throw new Error('Session expired. Please try again.');
      }

      const { accessToken, sessionId } = window.modalSessionData;
      
      // Validate that we have the necessary IDs
      if (!deal?.privateId || !deal?.publicId) {
        throw new Error('Deal Private ID and Public ID are required for offer selection');
      }
      
      // Import offersService to select the offer
      const offersService = await import('../../../service/offersService.ts').then(module => module.default);
      const response = await offersService.selectOffer(backendOfferId, amount, accessToken, sessionId, deal.privateId, deal.publicId);
      
      if (!response.data) {
        throw new Error('Failed to select offer');
      }

      console.log('✅ Offer selected successfully:', response.data);
      
      // Get auth token for subsequent operations
      const token = getToken();
      
      // Send contract communication after successful offer selection
      console.log('🔄 Sending contract communication...');
      if (token && deal?.privateId) {
        try {
          await sendContractCommunication(token, offer, currentOffer);
          console.log('✅ Contract communication sent successfully');
        } catch (contractError) {
          console.error('❌ Error sending contract communication:', contractError);
          // Don't fail the whole process if contract communication fails
        }
      }
      
      // Update deal status to CONTRACT after successful offer selection
      console.log('🔄 Updating deal status to CONTRACT after offer selection...');
      if (token && deal?.privateId) {
        try {
          await dealService.updateDealStatus(token, deal.privateId, 'CONTRACT');
          await dealService.updateDealSubstatus(token, deal.privateId, 'CONTRACT_SENT');
          console.log('✅ Deal status updated to CONTRACT successfully');
        } catch (statusError) {
          console.error('❌ Error updating deal status to CONTRACT:', statusError);
          // Don't fail the whole process if status update fails
        }
      }
      
      // Añadir la petición deal-addendums después del flujo normal
      console.log('🔄 Sending deal-addendum request for contract flow...');
      if (token && deal?.privateId) {
        try {
          await dealService.api.post(
            '/deal-service/api/deal-addendums',
            {
              deal_id: deal.privateId,
              has_addendum: false // Addendum checkbox is NOT active
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          console.log('✅ Deal-addendum request sent successfully for contract flow');
        } catch (addendumError) {
          console.error('❌ Error sending deal-addendum request for contract flow:', addendumError);
          // Don't fail the whole process if this request fails
        }
      }

      // Clear loading state and move to blank check upload step
      setModalSelectLoading(prev => ({ ...prev, [offerId]: false }));
      console.log('✅ Contract flow completed, moving to blank check step...');
      setModalStep(2);
      
    } catch (error) {
      console.error('❌ Error in contract select offer flow:', error);
      setModalSelectLoading(prev => ({ ...prev, [offerId]: false }));
      throw error;
    }
  };

  // Función para manejar la selección de offer solo con flujo de Addendum (sin contract)
  const handleAddendumSelectOffer = async (offerId, token) => {
    // Set loading state at the start
    setModalSelectLoading(prev => ({ ...prev, [offerId]: true }));
    
    try {
      console.log('🔄 Starting addendum flow for offer:', offerId);
      
      if (!window.modalSessionData) {
        console.error('Session expired. Please try again.');
        setModalSelectLoading(prev => ({ ...prev, [offerId]: false }));
        return;
      }

      const { accessToken, sessionId, dealPrivateId } = window.modalSessionData;
      
      // Find the offer to get the default amount
      const offer = modalOffers.find(o => o.id === offerId);
      if (!offer) {
        console.error('Offer not found.');
        return;
      }
      
      // Update offer if modified, then select it
      const { offerId: finalOfferId, backendOfferId, wasUpdated } = await updateOfferIfModified(offerId, offer);
      
      if (wasUpdated) {
        console.log('✅ Offer updated for addendum flow, using ID:', finalOfferId);
      }
      
      // Get the current (possibly edited) offer from calculations hook
      const currentOffer = getCurrentOffer(offer);
      
      // Get amount from calculated offer or use default offer amount
      const amount = currentOffer.advance_amount || offer.advance_amount;
      
      if (!amount || amount <= 0) {
        console.error('Please specify a valid amount for the selected offer.');
        return;
      }

      console.log(`Selecting offer ${finalOfferId} with amount ${amount} for addendum flow`);

      // Step 1: Select the offer (same as normal flow but without contract communication)
      console.log(`Using backend offer ID: ${backendOfferId} for frontend ID: ${finalOfferId}`);
      
      // Validate that we have the necessary IDs
      if (!deal?.privateId || !deal?.publicId) {
        throw new Error('Deal Private ID and Public ID are required for offer selection');
      }
      
      // Import offersService to select the offer
      const offersService = await import('../../../service/offersService.ts').then(module => module.default);
      const response = await offersService.selectOffer(backendOfferId, amount, accessToken, sessionId, deal.privateId, deal.publicId);
      
      if (!response.data) {
        throw new Error('Failed to select offer');
      }

      console.log('Offer selected successfully for addendum flow:', response.data);

      // Step 2: Execute addendum flow instead of contract flow
      // Pass the original offer ID so handleAddendumFlow can find it in modalOffers
      await handleAddendumFlow(offerId, token, wasUpdated ? currentOffer : null);

      // Step 3: Update deal status (similar to contract flow but maybe different status)
      console.log('🔄 Updating deal status after addendum...');
      if (token && deal?.privateId) {
        try {
          // For addendum flow, we might want to keep it as CONTRACT or use a different status
          // Using CONTRACT to maintain consistency with the normal flow
          await dealService.updateDealStatus(token, deal.privateId, 'CONTRACT');
          console.log('✅ Deal status updated successfully after addendum');
        } catch (statusError) {
          console.error('❌ Error updating deal status after addendum:', statusError);
          // Don't fail the whole process if status update fails
        }
      }

      // Step 4: Send deal-addendum request
      console.log('🔄 Sending deal-addendum request...');
      if (token && deal?.privateId) {
        try {
          await dealService.api.post(
            '/deal-service/api/deal-addendums',
            {
              deal_id: deal.privateId,
              has_addendum: true // Addendum checkbox is active
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          console.log('✅ Deal-addendum request sent successfully');
        } catch (addendumError) {
          console.error('❌ Error sending deal-addendum request:', addendumError);
          // Don't fail the whole process if this request fails
        }
      }

      console.log('✅ Offer selected successfully and addendum communication sent!');
      
      // Clear loading state and move to blank check upload step
      setModalSelectLoading(prev => ({ ...prev, [offerId]: false }));
      console.log('✅ Addendum flow completed, moving to blank check step...');
      setModalStep(2);
      
    } catch (error) {
      console.error('❌ Error in addendum select offer flow:', error);
      setModalSelectLoading(prev => ({ ...prev, [offerId]: false }));
      throw error;
    }
  };

  // Función para manejar el flujo de Addendum
  const handleAddendumFlow = async (offerId, token, modifiedOffer = null) => {
    try {
      if (!token) {
        throw new Error('Authentication token not available');
      }

      if (!deal?.privateId) {
        console.error('❌ No deal privateId available for addendum flow');
        throw new Error('Deal Private ID is required for addendum and contract communication');
      }

      console.log('🔄 Starting addendum flow for offer:', offerId);

      // Step 1: Get both templates
      const [addendumTemplateResponse, contractTemplateResponse] = await Promise.all([
        TemplateService.getAddendumState1Template(token),
        TemplateService.getFundingDocumentTemplate(token)
      ]);
      
      const addendumTemplates = addendumTemplateResponse.data;
      const contractTemplates = contractTemplateResponse.data;

      if (!addendumTemplates || addendumTemplates.length === 0) {
        throw new Error('No addendum template found');
      }
      
      if (!contractTemplates || contractTemplates.length === 0) {
        throw new Error('No contract template found');
      }
      
      const addendumTemplate = addendumTemplates[0];
      const contractTemplate = contractTemplates[0];
      
      console.log('📄 Templates received - Addendum:', addendumTemplate.name, 'Contract:', contractTemplate.name);

      // Step 2: Get the selected offer data
      // If a modified offer was created and passed, use it; otherwise, get from modalOffers
      let currentOfferData;
      let selectedOfferData; // Declare outside the conditional

      if (modifiedOffer) {
        console.log('Using modified offer data:', modifiedOffer);
        currentOfferData = modifiedOffer;
        selectedOfferData = modifiedOffer; // Use the modified offer as selectedOfferData
      } else {
        selectedOfferData = modalOffers.find(o => o.id === offerId);
        if (!selectedOfferData) {
          throw new Error('Selected offer not found');
        }
        // Get the current (possibly edited) offer from calculations hook
        currentOfferData = getCurrentOffer(selectedOfferData);
      }

      // Step 3: Get contacts from deal owners
      const contacts = getContactsFromOwner(deal);
      
      // Step 4: Generate unique request ID (single ID for the entire request)
      const requestId = generateUniqueRequestId(deal);

      // Step 5: Create shared template values for addendum
      const addendumTemplateValues = {
        referenceId: String(deal?.publicId || deal?.id || '').trim(),
        term: String(currentOfferData.term_duration_months || currentOfferData.term || 0).trim(),
        factor_rate: String(currentOfferData.factor || 0).trim(),
        advance_amount: String(currentOfferData.advance_amount || currentOfferData.advanceAmount || 0).trim(),
        legal_name: String(deal?.legalName || deal?.company_name || '').trim(),
        dba_name: String(deal?.dbaName || deal?.dba_name || deal?.companyName || '').trim(),
        contract_date: new Date().toISOString().split('T')[0] // Format: YYYY-MM-DD
      };

      // Step 6: Create contract template values (more comprehensive)
      const contractTemplateValues = {
        referenceId: String(deal?.publicId || deal?.id || '').trim(),
        prePaymentAddendum: true, // Since this is addendum flow
        legal_name: String(deal?.legalName || deal?.company_name || '').trim(),
        dba_name: String(deal?.dbaName || deal?.dba_name || deal?.companyName || '').trim(),
        business_structure: String(deal?.businessStructure || 'Other').trim(),
        state_of_incorporation: String(deal?.stateOfIncorporation || deal?.state || '').trim(),
        first_name: String(deal?.owners?.[0]?.firstName || deal?.owners?.[0]?.first_name || '').trim(),
        last_name: String(deal?.owners?.[0]?.lastName || deal?.owners?.[0]?.last_name || '').trim(),
        owner2_full_name: deal?.owners?.[1] ? String(getOwnerFullName(deal.owners[1])).trim() : '',
        email_1: String(contacts[0]?.email || '').trim(),
        mobile_phone: String(deal?.owners?.[0]?.phone || deal?.owners?.[0]?.mobile_phone || '').trim(),
        address: String(deal?.address || deal?.businessAddress || '').trim(),
        city: String(deal?.city || '').trim(),
        state: String(deal?.state || '').trim(),
        zip: String(deal?.zip || deal?.zipCode || '').trim(),
        advance_amount: String(modalEditingAmounts[offerId] || selectedOfferData.advance_amount || selectedOfferData.advanceAmount || 0).trim(),
        payback_amount: String(selectedOfferData.gross_payback || selectedOfferData.payback || 0).trim(),
        net_funded_amount: String(selectedOfferData.net_payback || selectedOfferData.netAdvanceAmount || 0).trim(),
        daily_ach_amount: String(selectedOfferData.daily_payment || 0).trim(),
        origination_fee: String(selectedOfferData.processing_fee || selectedOfferData.origination_fee || 0).trim(),
        gross_percentage: String(selectedOfferData.factor || 0).trim(),
        sum_of_deductions: "0",
        combined_balance: "0",
        third_party_amount: "0"
      };

      // Step 7: Build the payload following the Postman structure
      const addendumContractPayload = {
        requestId: requestId,
        privateDealId: deal?.privateId,
        contacts: contacts,
        addendumTemplate: {
          action: DOCUMENT_TYPE_ADDENDUM,
          templates: [{
            id: String(addendumTemplate.id || '').trim() + '.docx', //Why do I need to manually add the document's file extension???
            name: String(addendumTemplate.name || '').trim(),
            channel: String(addendumTemplate.channel || 'EMAIL').trim(),
            templateType: String(addendumTemplate.templateType || 'HTML').trim(),
            subject: addendumTemplate.subject ? String(addendumTemplate.subject).trim() : null,
            body: String(addendumTemplate.body || '').trim(),
            values: addendumTemplateValues
          }]
        },
        contractTemplate: {
          action: DOCUMENT_TYPE_CONTRACT,
          templates: [{
            id: String(contractTemplate.id || '').trim(),
            name: String(contractTemplate.name || '').trim(),
            channel: String(contractTemplate.channel || 'EMAIL').trim(),
            templateType: String(contractTemplate.templateType || 'HTML').trim(),
            subject: contractTemplate.subject ? String(contractTemplate.subject).trim() : null,
            body: String(contractTemplate.body || '').trim(),
            values: contractTemplateValues
          }]
        }
      };

      console.log('📦 Addendum-Contract payload:', JSON.stringify(addendumContractPayload, null, 2));

      // Step 8: Send the combined communication using the updated dispatchWithContract method
      console.log('🚀 Dispatching addendum and contract communications...');
      const response = await CommunicationService.dispatchWithContract(
        token,
        addendumContractPayload
      );

      console.log('✅ Communications dispatched successfully:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Error in addendum flow:', error);
      throw error;
    }
  };

  const getContactsFromOwner = (deal) => {
    const contacts = [];

    if (deal?.owners && Array.isArray(deal.owners)) {
      for (const owner of deal.owners) {
        const ownerEmail = getPrimaryEmailContact(owner);
        
        if (ownerEmail) {
          const ownerFullName = getOwnerFullName(owner);
          const ownerId = owner.id || owner.ownerId || owner.owner_id || `owner-${Date.now()}`;
          
          contacts.push({
            id: String(ownerId).trim(),
            name: String(ownerFullName).trim(),
            email: String(ownerEmail.value).trim(),
            phone: owner.phone || '',
            channels: ["EMAIL"]
          });
        }
      }
    }
    
    if (contacts.length === 0) {
      throw new Error('No valid email contacts found for addendum communication');
    }

    return contacts;
  }

  const generateUniqueRequestId = (deal) => {
    return `${Date.now()}-${deal?.publicId || deal?.id || 'unknown'}`;;
  }

  // Function to send contract communication (similar to addendum flow but contract only)
  const sendContractCommunication = async (token, offer, currentOffer) => {
    try {
      if (!token) {
        throw new Error('Authentication token not available');
      }

      if (!deal?.privateId) {
        console.error('❌ No deal privateId available for contract communication');
        throw new Error('Deal Private ID is required for contract communication');
      }

      console.log('🔄 Starting contract communication for offer:', offer.id);

      // Step 1: Get contract template
      const contractTemplateResponse = await TemplateService.getFundingDocumentTemplate(token);
      const contractTemplates = contractTemplateResponse.data;

      if (!contractTemplates || contractTemplates.length === 0) {
        throw new Error('No contract template found');
      }
      
      const contractTemplate = contractTemplates[0];
      console.log('📄 Contract template received:', contractTemplate.name);

      // Step 2: Get contacts from deal owners
      const contacts = getContactsFromOwner(deal);
      
      // Step 3: Generate unique request ID
      const requestId = generateUniqueRequestId(deal);

      // Step 4: Create contract template values (similar to addendum flow)
      const contractTemplateValues = {
        referenceId: String(deal?.publicId || deal?.id || '').trim(),
        prePaymentAddendum: false, // No addendum in this flow
        legal_name: String(deal?.legalName || deal?.company_name || '').trim(),
        dba_name: String(deal?.dbaName || deal?.dba_name || deal?.companyName || '').trim(),
        business_structure: String(deal?.businessStructure || 'Other').trim(),
        state_of_incorporation: String(deal?.stateOfIncorporation || deal?.state || '').trim(),
        first_name: String(deal?.owners?.[0]?.firstName || deal?.owners?.[0]?.first_name || '').trim(),
        last_name: String(deal?.owners?.[0]?.lastName || deal?.owners?.[0]?.last_name || '').trim(),
        owner2_full_name: deal?.owners?.[1] ? String(getOwnerFullName(deal.owners[1])).trim() : '',
        email_1: String(contacts[0]?.email || '').trim(),
        mobile_phone: String(deal?.owners?.[0]?.phone || deal?.owners?.[0]?.mobile_phone || '').trim(),
        address: String(deal?.address || deal?.businessAddress || '').trim(),
        city: String(deal?.city || '').trim(),
        state: String(deal?.state || '').trim(),
        zip: String(deal?.zip || deal?.zipCode || '').trim(),
        advance_amount: String(currentOffer.advance_amount || offer.advance_amount || 0).trim(),
        payback_amount: String(currentOffer.gross_payback || offer.gross_payback || offer.payback || 0).trim(),
        net_funded_amount: String(currentOffer.net_payback || offer.net_payback || offer.netAdvanceAmount || 0).trim(),
        daily_ach_amount: String(currentOffer.daily_payment || offer.daily_payment || 0).trim(),
        origination_fee: String(currentOffer.processing_fee || offer.processing_fee || offer.origination_fee || 0).trim(),
        gross_percentage: String(currentOffer.factor || offer.factor || 0).trim(),
        sum_of_deductions: "0",
        combined_balance: "0",
        third_party_amount: "0"
      };

      // Step 5: Build the payload for contract-only communication
      const contractPayload = {
        requestId: requestId,
        action: DOCUMENT_TYPE_CONTRACT,
        dealId: deal?.privateId,
        contacts: contacts,
        templates: [{
          id: String(contractTemplate.id || '').trim(),
          name: String(contractTemplate.name || '').trim(),
          channel: String(contractTemplate.channel || 'EMAIL').trim(),
          templateType: String(contractTemplate.templateType || 'HTML').trim(),
          subject: contractTemplate.subject ? String(contractTemplate.subject).trim() : null,
          body: String(contractTemplate.body || '').trim(),
          values: contractTemplateValues
        }]
      };

      console.log('📦 Contract-only payload:', JSON.stringify(contractPayload, null, 2));

      // Step 6: Send the contract communication using the regular dispatch method
      console.log('🚀 Dispatching contract communication...');
      const response = await CommunicationService.dispatch(
        token,
        contractPayload
      );

      console.log('✅ Contract communication dispatched successfully:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Error in contract communication:', error);
      throw error;
    }
  };

  const populateDocumentTemplate = () => {}

  // Función para guardar los cambios
  const handleSaveOffersEdit = async () => {
    try {
      setUpdateOffersLoading(true);
      setUpdateOffersError(null);
      
      const token = getToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      if (!deal || !deal.privateId) {
        throw new Error('Deal private ID not available');
      }

      // Encontrar offers modificados
      const modifiedOffers = editingOffers.filter(editedOffer => {
        const originalOffer = originalOffersData.find(orig => orig.id === editedOffer.id);
        if (!originalOffer) return true;
        
        // Verificar si algún campo fue modificado
        return ['advance_amount', 'gross_advance_amount', 'net_payback', 'factor', 'gross_payback'].some(key => {
          return editedOffer[key] !== originalOffer[key];
        });
      });

      console.log('🔍 Modified offers found:', modifiedOffers.length);

      // Solo proceder si hay cambios reales
      if (modifiedOffers.length > 0) {
        console.log('📝 Updating modified offers...');
        
        // Actualizar offers modificados
        for (const modifiedOffer of modifiedOffers) {
          console.log('📝 Updating offer:', modifiedOffer.id, 'with data:', modifiedOffer);
          await dealService.updateDealOffer(token, deal.privateId, modifiedOffer.id, {
            advance_amount: modifiedOffer.advance_amount,
            gross_advance_amount: modifiedOffer.gross_advance_amount,
            net_payback: modifiedOffer.net_payback,
            factor: modifiedOffer.factor,
            gross_payback: modifiedOffer.gross_payback || (modifiedOffer.gross_advance_amount * modifiedOffer.factor),
            term_duration_months: modifiedOffer.term_duration_months || modifiedOffer.term,
            monthly_payment: modifiedOffer.monthly_payment,
            processing_fee: modifiedOffer.processing_fee || 0
          });
        }

        console.log('📧 Sending updated pre-approval notifications...');
        // Seguir el flujo exacto de handleConfirmPreApproval: SMS y Email (que hacen _PREAPPROVAL_EMAIL_ → dispatch)
        // El email automáticamente genera un nuevo magiclink que cancela el anterior
        if (sendPreApprovalSms) {
          await sendPreApprovalSms(token);
        }

        if (sendPreApprovalEmails) {
          await sendPreApprovalEmails(token);
        }

        console.log('✅ All notifications sent successfully (new magic link generated automatically with email)');
      } else {
        console.log('ℹ️ No changes detected, skipping magiclink and notifications');
      }

      // Recargar los offers para mostrar los datos actualizados
      if (loadDealOffers) {
        await loadDealOffers();
      }

      setIsEditingOffers(false);
      setUpdateOffersSuccess(true);
      
      // Mostrar mensaje específico dependiendo de si hubo cambios
      console.log('🎉 Process completed successfully');
      
      // Recargar la página después de un delay más largo para permitir que se completen todas las peticiones
      setTimeout(() => {
        //window.location.reload();
      }, 5000);

    } catch (error) {
      console.error('Error updating offers:', error);
      setUpdateOffersError(error.message || 'Error updating offers');
    } finally {
      setUpdateOffersLoading(false);
    }
  };

  // Función para manejar la subida del blank check
  const handleBlankCheckUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!deal || !deal.privateId) {
      console.error('Deal privateId is required for file upload');
      setBlankCheckUploadError('Deal information is missing');
      return;
    }

    try {
      setUploadingBlankCheck(true);
      setBlankCheckUploadError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Get file extension
      const fileExtension = file.name.split('.').pop();
      
      // Create custom file name: legalName - publicId - blank-check
      const legalName = deal.legalName || deal.company_name || 'Unknown';
      const publicId = deal.publicId || deal.id || 'Unknown';
      const customFileName = `${legalName} - ${publicId} - blank-check.${fileExtension}`;
      
      console.log('📄 Uploading blank check with custom name:', customFileName);

      // Create a new File object with the custom name
      const renamedFile = new File([file], customFileName, { type: file.type });

      // Upload document using documentsApi
      const { documentId, details } = await documentsApi.uploadDocument({
        file: renamedFile,
        category: 'blank_check',
        originType: 'blank_check',
        originId: deal.privateId,
        description: `Blank check for deal ${deal.publicId}`,
        tags: ['blank_check', 'contract'],
        onUploadProgress: (progress) => {
          console.log('Upload progress:', progress.percent + '%');
        }
      });

      console.log('✅ Blank check uploaded successfully:', { documentId, details });

      // Update state
      setBlankCheckFile({
        fileName: file.name,
        documentId: documentId,
        details: details
      });

      // Update has-blank-check to true
      await updateHasBlankCheck(true);

      // Close modal and reload after successful upload
      handleModalClose();
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('❌ Error uploading blank check:', error);
      setBlankCheckUploadError(error.message || 'Failed to upload blank check');
    } finally {
      setUploadingBlankCheck(false);
    }
  };

  // Función para actualizar has-blank-check
  const updateHasBlankCheck = async (hasBlankCheck) => {
    try {
      if (!deal || !deal.privateId) {
        throw new Error('Deal privateId is required');
      }

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log(`🔄 Updating has-blank-check to ${hasBlankCheck}...`);

      await dealService.api.patch(
        `/deal-service/api/deals/${deal.privateId}/has-blank-check`,
        {
          hasBlankCheck: hasBlankCheck
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log(`✅ has-blank-check updated to ${hasBlankCheck} successfully`);

    } catch (error) {
      console.error('❌ Error updating has-blank-check:', error);
      throw error;
    }
  };

  // Función para saltar la subida del blank check
  const handleSkipBlankCheck = async () => {
    try {
      setUploadingBlankCheck(true);
      setBlankCheckUploadError(null);

      // Update has-blank-check to false
      await updateHasBlankCheck(false);

      // Close modal and reload
      handleModalClose();
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('❌ Error skipping blank check:', error);
      setBlankCheckUploadError(error.message || 'Failed to skip blank check');
    } finally {
      setUploadingBlankCheck(false);
    }
  };

  return (
    <>
    <Row>
      <Col>
        <Card className="shadow-sm">
          <CardHeader className="bg-info text-white">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ni ni-check-bold mr-2"></i>
                <h5 className="mb-0">Pre-Approved Offers</h5>
              </div>
              {/* Botón de Edit solo cuando hasSelection es false y no está en modo pendingPreApproval */}
              {!pendingPreApproval && !hasSelection && offers.length > 0 && (
                !isEditingOffers ? (
                  <Button 
                    color="light" 
                    size="sm" 
                    onClick={handleEditOffers}
                    disabled={updateOffersLoading || offersLoading}
                  >
                    <i className="ni ni-ruler-pencil mr-1"></i>
                    Edit
                  </Button>
                ) : (
                  <div>
                    <Button 
                      color="success" 
                      size="sm" 
                      onClick={handleSaveOffersEdit}
                      disabled={updateOffersLoading}
                      className="mr-2"
                    >
                      {updateOffersLoading ? (
                        <>
                          <Spinner size="sm" className="mr-1" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="ni ni-check-bold mr-1"></i>
                          Save
                        </>
                      )}
                    </Button>
                    <Button 
                      color="secondary" 
                      size="sm" 
                      onClick={handleCancelOffersEdit}
                      disabled={updateOffersLoading}
                    >
                      <i className="ni ni-fat-remove mr-1"></i>
                      Cancel
                    </Button>
                  </div>
                )
              )}
            </div>
          </CardHeader>
          <CardBody>
            {/* Mensajes de error y éxito para la edición de offers */}
            {updateOffersError && (
              <Alert color="danger" className="mb-3">
                <i className="ni ni-support-16 mr-2"></i>
                {updateOffersError}
              </Alert>
            )}
            {updateOffersSuccess && (
              <Alert color="success" className="mb-3">
                <i className="ni ni-check-bold mr-2"></i>
                Offers updated successfully. New magic link generated and notifications sent.
              </Alert>
            )}
            
            {pendingPreApproval && (
              <div className="mb-3">
                <Alert color="info">
                  <strong>Review and Edit Offers:</strong> You can modify the offer values below before confirming the pre-approval.
                </Alert>
              </div>
            )}
            
            {isEditingOffers && !pendingPreApproval && (
              <div className="mb-3">
                <Alert color="info">
                  <strong>Editing Offers:</strong> You can modify the offer values below. Click Save to apply changes or Cancel to discard them.
                </Alert>
              </div>
            )}

            {/* Selected Offer Table - Only show when deal is PRE_APPROVED and has a selection */}
            {!pendingPreApproval && hasSelection && selectedOffer && (
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <i className="ni ni-favourite-28 text-muted mr-2" style={{ fontSize: '1.5rem' }}></i>
                  <h6 className="mb-0 text-dark font-weight-bold">Selected Offer</h6>
                </div>
                
                {selectionLoading ? (
                  <div className="text-center py-3">
                    <Spinner color="primary" size="sm" />
                    <span className="ml-2 text-muted">Loading selected offer...</span>
                  </div>
                ) : (
                  <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table className="align-items-center table-flush" responsive size="sm" style={{ minWidth: '1000px', fontSize: '0.875rem' }}>
                      <thead className="thead-light">
                        <tr>
                          <th scope="col" style={{ minWidth: '110px' }}>Advance Amount</th>
                          <th scope="col" style={{ minWidth: '110px' }}>Gross Advance Amount</th>
                          <th scope="col" style={{ minWidth: '110px' }}>Payback</th>
                          <th scope="col" style={{ minWidth: '60px' }}>Factor</th>
                          <th scope="col" style={{ minWidth: '90px' }}>Term Duration</th>
                          <th scope="col" style={{ minWidth: '110px' }}>Monthly Payment</th>
                          <th scope="col" style={{ minWidth: '90px' }}>Processing Fee</th>
                          <th scope="col" style={{ minWidth: '100px' }}>Selected Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #6c757d' }}>
                          <td className="font-weight-bold">
                            {formatCurrency(selectedOffer.advance_amount || selectedOffer.advanceAmount)}
                          </td>
                          <td className="font-weight-bold">
                            {formatCurrency(selectedOffer.gross_advance_amount || selectedOffer.grossAdvanceAmount)}
                          </td>
                          <td>
                            {formatCurrency(selectedOffer.net_payback || selectedOffer.netAdvanceAmount)}
                          </td>
                          <td>
                            {selectedOffer.factor || 'N/A'}
                          </td>
                          <td>
                            {selectedOffer.term_duration_months || selectedOffer.term ? `${selectedOffer.term_duration_months || selectedOffer.term} months` : 'N/A'}
                          </td>
                          <td>
                            {formatCurrency(selectedOffer.monthly_payment || selectedOffer.monthlyGrossMargin)}
                          </td>
                          <td>
                            {formatCurrency(selectedOffer.processing_fee || 0)}
                          </td>
                          <td>
                            {formatDate(selectedOffer.updatedAt || selectedOffer.updated_at || selectedOffer.createdAt || selectedOffer.created_at)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}
                
                <hr className="my-4" />
              </div>
            )}

            {/* Pre-Approved Offers Section Title */}
            {!pendingPreApproval && hasSelection && (
              <div className="d-flex align-items-center mb-3">
                <i className="ni ni-bullet-list-67 text-info mr-2"></i>
                <h6 className="mb-0 text-muted">All Available Offers</h6>
              </div>
            )}
            
            {offersLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <span className="ml-2">Loading offers...</span>
              </div>
            ) : (pendingPreApproval ? editedOffers : (isEditingOffers ? editingOffers : offers)).length > 0 ? (
              <>
                <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table className="align-items-center table-flush" responsive style={{ minWidth: '1000px', fontSize: '0.875rem' }}>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col" style={{ minWidth: '110px' }}>Advance Amount</th>
                        <th scope="col" style={{ minWidth: '110px' }}>Gross Advance Amount</th>
                        <th scope="col" style={{ minWidth: '110px' }}>Payback</th>
                        <th scope="col" style={{ minWidth: '60px' }}>Factor</th>
                        <th scope="col" style={{ minWidth: '90px' }}>Term Duration</th>
                        <th scope="col" style={{ minWidth: '110px' }}>Monthly Payment</th>
                        <th scope="col" style={{ minWidth: '90px' }}>Processing Fee</th>
                        {!pendingPreApproval && !isEditingOffers && <th scope="col" style={{ minWidth: '100px' }}>Created</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(pendingPreApproval ? editedOffers : (isEditingOffers ? editingOffers : offers))
                        .filter(offer => {
                          // Only filter when there are MORE than 3 offers and there's a selected offer
                          if (!pendingPreApproval && !isEditingOffers && hasSelection && selectedOffer) {
                            if (offers.length > 3) {
                              // Filter out the selected offer by comparing IDs
                              return offer.id !== selectedOffer.id;
                            }
                          }
                          return true;
                        })
                        .map((offer, index) => (
                          <tr key={offer.id || offer.offerId || index}>
                            <td>
                              {formatCurrency(offer.advance_amount || offer.advanceAmount)}
                            </td>
                            <td>
                              {(pendingPreApproval || isEditingOffers) ? (
                                <Input
                                  type="text"
                                  size="sm"
                                  style={{ width: '90px', maxWidth: '90px' }}
                                  value={formatInputValue(offer.gross_advance_amount || offer.grossAdvanceAmount || 0)}
                                  onChange={(e) => {
                                    const rawValue = e.target.value.replace(/,/g, '');
                                    pendingPreApproval 
                                      ? handlePendingOfferChange(offer.id, 'gross_advance_amount', rawValue)
                                      : handleEditingOfferChange(offer.id, 'gross_advance_amount', rawValue);
                                  }}
                                />
                              ) : (
                                formatCurrency(offer.gross_advance_amount || offer.grossAdvanceAmount)
                              )}
                            </td>
                            <td>
                              {formatCurrency(offer.net_payback || offer.netAdvanceAmount)}
                            </td>
                            <td>
                              {(pendingPreApproval || isEditingOffers) ? (
                                <Input
                                  type="text"
                                  size="sm"
                                  style={{ width: '50px', maxWidth: '50px' }}
                                  value={offer.factor || 0}
                                  onChange={(e) => {
                                    const rawValue = e.target.value.replace(/,/g, '');
                                    pendingPreApproval 
                                      ? handlePendingOfferChange(offer.id, 'factor', rawValue)
                                      : handleEditingOfferChange(offer.id, 'factor', rawValue);
                                  }}
                                />
                              ) : (
                                offer.factor || 'N/A'
                              )}
                            </td>
                            <td>
                              {offer.term_duration_months || offer.term ? `${offer.term_duration_months || offer.term} months` : 'N/A'}
                            </td>
                            <td>
                              {formatCurrency(offer.monthly_payment || offer.monthlyGrossMargin || 0)}
                            </td>
                            <td>
                              {formatCurrency(offer.processing_fee || 0)}
                            </td>
                            {!pendingPreApproval && !isEditingOffers && (
                              <td>
                                {formatDate(offer.createdAt || offer.created_at)}
                              </td>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>

                {/* Action Buttons for Pending Pre-Approval */}
                {pendingPreApproval && (
                  <div className="mt-4 d-flex justify-content-end gap-2">
                    <Button 
                      color="secondary" 
                      onClick={handleCancelPreApproval}
                      disabled={confirmPreApprovalLoading}
                    >
                      Cancel
                    </Button>
                    <Button 
                      color="success" 
                      onClick={handleConfirmPreApproval}
                      disabled={confirmPreApprovalLoading}
                    >
                      {confirmPreApprovalLoading ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Confirming...
                        </>
                      ) : (
                        'Confirm Pre-Approval'
                      )}
                    </Button>
                  </div>
                )}

                {/* Confirm on Behalf Button - Only show when deal is PRE_APPROVED and no selection */}
                {(() => {
                  const shouldShow = !pendingPreApproval && !offersLoading && !selectionLoading && deal?.status === 'OFFERS' && !hasSelection && offers.length > 0;
                  console.log('🔵 ConfirmOnBehalf button conditions:', {
                    pendingPreApproval,
                    offersLoading,
                    selectionLoading,
                    dealStatus: deal?.status,
                    hasSelection,
                    offersLength: offers.length,
                    shouldShow
                  });
                  return shouldShow;
                })() && (
                  <div className="mt-4 text-center">
                    <Button
                      color="info"
                      size="lg"
                      onClick={() => {
                        console.log('🔵 ConfirmOnBehalf button clicked!');
                        handleConfirmOnBehalf();
                      }}
                      disabled={generatingLink || modalLoading || isEditingOffers}
                      className="px-5"
                    >
                      {generatingLink || modalLoading ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          {generatingLink ? 'Generating Link...' : 'Loading Offers...'}
                        </>
                      ) : (
                        <>
                          <i className="ni ni-check-bold mr-2"></i>
                          Confirm on Behalf
                        </>
                      )}
                    </Button>
                    <p className="text-muted mt-2 small">
                      Select an offer on behalf of the client
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ni ni-money-coins text-muted" style={{ fontSize: '4rem', opacity: 0.3 }} />
                </div>
                <h4 className="text-muted mb-2">No offers available</h4>
                <p className="text-muted mb-4">No pre-approved offers have been generated for this deal yet.</p>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>

    {/* Offers Selection Modal */}
    <Modal isOpen={showOffersModal} toggle={handleModalClose} size="xl" centered>
      <ModalHeader toggle={modalStep === 1 ? handleModalClose : undefined}>
        <div className="d-flex align-items-center">
          <div className="icon icon-shape bg-primary rounded-circle shadow mr-3" style={{ color: 'white' }}>
            <i className={modalStep === 1 ? "ni ni-money-coins" : "ni ni-paper-diploma"} />
          </div>
          <div>
            <h4 className="mb-0">
              {modalStep === 1 ? 'Select Offer on Behalf' : 'Upload Blank Check'}
            </h4>
            <p className="text-muted mb-0 small">
              {modalStep === 1 
                ? 'Choose the best funding option for the client' 
                : 'Required document for contract funding'}
            </p>
          </div>
        </div>
      </ModalHeader>
      
      <ModalBody className="p-4">
        {/* Step 1: Select Offer */}
        {modalStep === 1 && (
          <>
            {modalError && (
              <Alert color="danger" className="mb-4">
                <i className="fas fa-exclamation-triangle mr-2" />
                {modalError}
              </Alert>
            )}

            {modalLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" className="mb-3" />
                <h5>Loading Offers...</h5>
                <p className="text-muted">Please wait while we fetch the available offers...</p>
              </div>
            ) : modalOffers.length > 0 ? (
          <>
            <div className="text-center mb-3">
              <h5 className="text-primary mb-1">
                <i className="ni ni-check-bold mr-2" />
                Available Funding Options
              </h5>
              <p className="text-muted mb-0">Select the preferred funding option on behalf of the client</p>
            </div>
            
            {/* Clean table structure */}
            <div className="table-responsive">
              <table className="table table-sm table-hover" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: '12%', textAlign: 'center' }}>Term Duration</th>
                    <th style={{ width: '14%', textAlign: 'center' }}>Advance Amount</th>
                    <th style={{ width: '16%', textAlign: 'center' }}>Gross Advance Amount</th>
                    <th style={{ width: '12%', textAlign: 'center' }}>Payback</th>
                    <th style={{ width: '8%', textAlign: 'center' }}>Factor</th>
                    <th style={{ width: '12%', textAlign: 'center' }}>Monthly Payment</th>
                    <th style={{ width: '10%', textAlign: 'center' }}>Processing Fee</th>
                    <th style={{ width: '16%', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {modalOffers.map((offer, index) => {
                    // Get the current offer with calculated values
                    const currentOffer = getCurrentOffer(offer);
                    
                    return (
                      <tr key={offer.id || index}>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <strong>{currentOffer.term_duration_months || currentOffer.term || 0} months</strong>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <strong>{formatCurrency(currentOffer.advance_amount || currentOffer.advanceAmount || 0)}</strong>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <div className="d-flex align-items-center justify-content-center">
                            <Button
                              color="outline-secondary"
                              size="sm"
                              className="mr-1"
                              style={{ padding: '0.25rem 0.4rem', fontSize: '0.75rem' }}
                              onClick={() => adjustGrossAdvanceAmount(offer, 'decrement')}
                              disabled={!canDecrement(offer) || Object.values(modalSelectLoading).some(loading => loading)}
                            >
                              −
                            </Button>
                            <span className="font-weight-bold mx-1" style={{ minWidth: '60px' }}>
                              {formatCurrency(currentOffer.gross_advance_amount || currentOffer.grossAdvanceAmount || 0)}
                            </span>
                            <Button
                              color="outline-secondary"
                              size="sm"
                              className="ml-1"
                              style={{ padding: '0.25rem 0.4rem', fontSize: '0.75rem' }}
                              onClick={() => adjustGrossAdvanceAmount(offer, 'increment')}
                              disabled={!canIncrement(offer) || Object.values(modalSelectLoading).some(loading => loading)}
                            >
                              +
                            </Button>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <strong>{formatCurrency(currentOffer.net_payback || currentOffer.netAdvanceAmount || 0)}</strong>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <strong>{currentOffer.factor ? currentOffer.factor.toFixed(2) : 'N/A'}</strong>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <strong>{formatCurrency(currentOffer.monthly_payment || currentOffer.monthlyGrossMargin || 0)}</strong>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <strong>{formatCurrency(currentOffer.processing_fee || 0)}</strong>
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Button 
                            color="primary" 
                            size="sm"
                            onClick={() => handleModalSelectOfferWithAddendum(offer.id)}
                            disabled={Object.values(modalSelectLoading).some(loading => loading)}
                            style={{ padding: '0.375rem 0.75rem', fontSize: '0.8rem' }}
                          >
                            {modalSelectLoading[offer.id] ? (
                              <>
                                <Spinner size="sm" className="mr-1" />
                                Selecting...
                              </>
                            ) : (
                              'Select'
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Funding Conditions */}
            <div className="mt-3 bg-light rounded p-2">
              <h6 className="text-muted mb-2">Funding Conditions:</h6>
              <Row>
                <Col md="4">
                  <div className="mb-1">
                    <i className="ni ni-check-bold text-success mr-2" />
                    <small>Void check</small>
                  </div>
                </Col>
                <Col md="4">
                  <div className="mb-1">
                    <i className="ni ni-check-bold text-success mr-2" />
                    <small>Fully executed contracts</small>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Addendum Checkbox */}
            <div className="mt-3 d-flex align-items-center justify-content-center">
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="modalAddendumCheckbox"
                  checked={addendumChecked}
                  onChange={(e) => setAddendumChecked(e.target.checked)}
                  disabled={Object.values(modalSelectLoading).some(loading => loading)}
                />
                <label className="custom-control-label" htmlFor="modalAddendumCheckbox">
                  <span className="text-dark font-weight-bold">Addendum</span>
                </label>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="ni ni-money-coins" style={{ fontSize: '3rem', color: '#ccc' }}></i>
            <h5 className="text-muted mt-3">No offers available</h5>
            <p className="text-muted">Unable to load offers at this time.</p>
          </div>
        )}
          </>
        )}

        {/* Step 2: Blank Check Upload */}
        {modalStep === 2 && (
          <div className="text-center py-5">
            {/* Warning Icon and Message */}
            <div className="mb-4">
              <div 
                className="icon icon-shape bg-gradient-warning rounded-circle shadow mx-auto mb-3"
                style={{ 
                  width: '80px', 
                  height: '80px',
                  background: 'linear-gradient(135deg, #fb6340, #fbb140)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="ni ni-bell-55 text-white" style={{ fontSize: '2.5rem' }} />
              </div>
              
              <h3 className="text-warning mb-3" style={{ fontWeight: '700', fontSize: '1.5rem' }}>
                BLANK CHECK IS REQUIRED BEFORE CONTRACT FUNDING CAN OCCUR
              </h3>
              
              <p className="text-muted mb-4" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                Please upload a blank check to continue with the contract funding process.
              </p>
            </div>

            {/* Error Message */}
            {blankCheckUploadError && (
              <Alert color="danger" className="mb-4">
                <i className="ni ni-support-16 mr-2" />
                {blankCheckUploadError}
              </Alert>
            )}

            {/* Success Message */}
            {blankCheckFile && (
              <Alert color="success" className="mb-4">
                <i className="ni ni-check-bold mr-2" />
                File uploaded successfully: {blankCheckFile.fileName}
              </Alert>
            )}

            {/* Upload Section */}
            <div className="mb-4">
              <input
                type="file"
                id="blankCheckInput"
                onChange={handleBlankCheckUpload}
                style={{ display: 'none' }}
                aria-label="Upload blank check"
                disabled={uploadingBlankCheck}
                accept=".pdf,.png,.jpg,.jpeg,.gif"
              />
              <Button
                color="primary"
                size="lg"
                onClick={() => document.getElementById('blankCheckInput').click()}
                disabled={uploadingBlankCheck}
                className="px-5"
                style={{ 
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  padding: '0.75rem 2rem'
                }}
              >
                {uploadingBlankCheck ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ni ni-cloud-upload-96 mr-2" />
                    Upload Blank Check
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </ModalBody>
      
      <ModalFooter>
        {modalStep === 1 && (
          <Button color="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
        )}
        
        {/* Skip Button - In footer for step 2, small and almost imperceptible */}
        {modalStep === 2 && (
          <Button
            color="link"
            size="sm"
            onClick={handleSkipBlankCheck}
            disabled={uploadingBlankCheck}
            className="text-muted"
            style={{ 
              fontSize: '0.75rem',
              textDecoration: 'none',
              opacity: 0.5
            }}
          >
            Skip
          </Button>
        )}
      </ModalFooter>
    </Modal>
    </>
  );
};

export default PreApprovedTab;
